import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './CustomerView.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const BUCKET_LABELS = {
  total_registered:  'Total Registered',
  no_sales:          'No Sales Ever',
  at_least_one_sale: 'At Least 1 Sale',
  active_7d:         'Active — Last 7 Days',
  active_30d:        'Active — Last 30 Days',
  lapsed:            'Lapsed',
};

const StatCard = ({ label, value, sub, bucket, onClick }) => (
  <div className="cv-stat-card" onClick={() => onClick(bucket)} title="Click to view customers">
    <div className="cv-stat-label">{label}</div>
    <div className="cv-stat-value">{value ?? '—'}</div>
    {sub && <div className="cv-stat-sub">{sub}</div>}
  </div>
);

const DetailModal = ({ bucket, authHeader, onClose }) => {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    axios.get(`${API_URL}/reports/customer-view/detail?bucket=${bucket}`, { headers: authHeader })
      .then((res) => setRows(res.data))
      .catch(() => setError('Failed to load details.'))
      .finally(() => setLoading(false));
  }, [bucket]); // eslint-disable-line

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = [...rows].sort((a, b) => {
    const va = sortKey === 'days_since_joined' ? (a[sortKey] ?? 0) : (a[sortKey] ?? '').toLowerCase();
    const vb = sortKey === 'days_since_joined' ? (b[sortKey] ?? 0) : (b[sortKey] ?? '').toLowerCase();
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="cv-sort-icon cv-sort-icon--inactive">↕</span>;
    return <span className="cv-sort-icon">{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  return (
    <div className="cv-modal-overlay" onClick={onClose}>
      <div className="cv-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="cv-modal-header">
          <h3>{BUCKET_LABELS[bucket]}{!loading && ` (${rows.length})`}</h3>
          <button className="cv-close-button" onClick={onClose}>×</button>
        </div>

        <div className="cv-modal-body">
          {loading && <div className="cv-loading">Loading…</div>}
          {error   && <div className="cv-error">{error}</div>}
          {!loading && !error && (
            <table className="cv-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th className="cv-th-sortable" onClick={() => handleSort('name')}>
                    Name <SortIcon col="name" />
                  </th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>Address</th>
                  <th className="cv-th-sortable" onClick={() => handleSort('days_since_joined')}>
                    Days Since Joined <SortIcon col="days_since_joined" />
                  </th>
                </tr>
              </thead>
              <tbody>
                {sorted.length === 0 ? (
                  <tr><td colSpan={6} className="cv-empty">No customers in this bucket</td></tr>
                ) : (
                  sorted.map((r, i) => (
                    <tr key={i}>
                      <td className="cv-rank">{i + 1}</td>
                      <td>{r.name}</td>
                      <td>{r.phone}</td>
                      <td>{r.email}</td>
                      <td>{r.address}</td>
                      <td className="cv-num">{r.days_since_joined}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        <div className="cv-modal-actions">
          <button className="cv-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const CustomerView = () => {
  const { user } = useContext(AuthContext);
  const authHeader = { Authorization: `Bearer ${user?.token}` };

  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [activeBucket, setActiveBucket] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/reports/customer-view`, { headers: authHeader })
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.status === 403 ? 'Access denied.' : 'Failed to load report.'))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <div className="cv-loading">Loading…</div>;
  if (error)   return <div className="cv-error">{error}</div>;

  const s = data.summary;

  return (
    <div className="cv-container">
      <h2 className="cv-title">Customer View</h2>

      <div className="cv-stats-grid">
        <StatCard label="Total Registered"     value={s.total_registered}  bucket="total_registered"  onClick={setActiveBucket} />
        <StatCard label="No Sales Ever"         value={s.no_sales}          bucket="no_sales"          onClick={setActiveBucket} />
        <StatCard label="At Least 1 Sale"       value={s.at_least_one_sale} bucket="at_least_one_sale" onClick={setActiveBucket} />
        <StatCard label="Active — Last 7 Days"  value={s.active_7d}         bucket="active_7d"         onClick={setActiveBucket}
          sub={`of ${s.active_30d} active in 30 days`} />
        <StatCard label="Active — Last 30 Days" value={s.active_30d}        bucket="active_30d"        onClick={setActiveBucket} />
        <StatCard label="Lapsed"                value={s.lapsed}            bucket="lapsed"            onClick={setActiveBucket}
          sub="ordered before, none in 30 days" />
      </div>

      <h3 className="cv-section-title">Top 25 Customers — Last 30 Days</h3>
      <div className="cv-table-wrap">
        <table className="cv-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Customer</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Orders</th>
              <th>Amount (₹)</th>
            </tr>
          </thead>
          <tbody>
            {data.top25.length === 0 ? (
              <tr><td colSpan={6} className="cv-empty">No orders in the last 30 days</td></tr>
            ) : (
              data.top25.map((row, i) => (
                <tr key={i}>
                  <td className="cv-rank">{i + 1}</td>
                  <td>{row.customer_name}</td>
                  <td>{row.phone}</td>
                  <td>{row.email}</td>
                  <td className="cv-num">{row.order_count}</td>
                  <td className="cv-num">₹{Number(row.total_amount).toLocaleString('en-IN')}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {activeBucket && (
        <DetailModal
          bucket={activeBucket}
          authHeader={authHeader}
          onClose={() => setActiveBucket(null)}
        />
      )}
    </div>
  );
};

export default CustomerView;
